import React, { useState, useEffect, useRef } from 'react';
import AddressBookService from '../../services/AddressBookService';
import QRCodeService from '../../services/QRCodeService';
import QRScanner from './QRScanner';
import AddressDisplay, { notifyAddressBookUpdated } from '../AddressDisplay';

/**
 * AddressBookIcon Component
 * 
 * Compact address book icon for top-right placement throughout the app.
 * Provides quick access to address book management in a dropdown modal.
 */
const AddressBookIcon = () => {
  const [addressEntries, setAddressEntries] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [newAddress, setNewAddress] = useState('');
  const [newNickname, setNewNickname] = useState('');
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingNickname, setEditingNickname] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'add', 'qr-add', or 'import-export'
  const [qrData, setQrData] = useState('');
  const [parsedQRAddress, setParsedQRAddress] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scannedAddress, setScannedAddress] = useState(null); // Store scanned result
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadAddressEntries();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        resetForm();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadAddressEntries = () => {
    const entries = AddressBookService.getAllEntries();
    setAddressEntries(entries);
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const showError = (message) => {
    setError(message);
    setSuccess('');
    setTimeout(clearMessages, 5000);
  };

  const showSuccess = (message) => {
    setSuccess(message);
    setError('');
    setTimeout(clearMessages, 3000);
  };

  const resetForm = () => {
    setNewAddress('');
    setNewNickname('');
    setEditingAddress(null);
    setEditingNickname('');
    setQrData('');
    setParsedQRAddress(null);
    setIsScanning(false);
    setScannedAddress(null);
    clearMessages();
  };

  const handleQRDataChange = (value) => {
    setQrData(value);
    clearMessages();

    if (value.trim()) {
      const parsed = QRCodeService.parseAddressQR(value.trim());
      if (parsed) {
        setParsedQRAddress(parsed);
        setError('');
      } else {
        setParsedQRAddress(null);
        setError('Invalid QR code data. Please check the format.');
      }
    } else {
      setParsedQRAddress(null);
    }
  };

  const handleQRScanned = (scannedData) => {
    console.log('QR Code scanned:', scannedData);
    setIsScanning(false);

    // Parse the scanned data
    const parsed = QRCodeService.parseAddressQR(scannedData);
    if (parsed) {
      setScannedAddress(parsed);
      showSuccess('QR code scanned successfully!');
    } else {
      showError('Invalid QR code format. Please try again.');
    }
  };

  const handleQRScanError = (error) => {
    console.error('QR scan error:', error);
    setIsScanning(false);
    showError('Error scanning QR code. You can try again or enter data manually.');
  };

  const handleAddScannedAddress = () => {
    if (!scannedAddress) {
      showError('No scanned address available');
      return;
    }

    clearMessages();

    if (!AddressBookService.isValidAddress(scannedAddress.address)) {
      showError('Invalid address format in scanned QR code');
      return;
    }

    // Use the (possibly edited) nickname from the scanned address object
    const nickname = scannedAddress.editedName || scannedAddress.name || `Wallet ${scannedAddress.address.slice(0, 6)}...${scannedAddress.address.slice(-4)}`;
    const success = AddressBookService.setNickname(scannedAddress.address, nickname);

    if (success) {
      setScannedAddress(null);
      setIsScanning(false);
      loadAddressEntries();
      notifyAddressBookUpdated();
      showSuccess(`Added "${nickname}" to address book`);
      setActiveTab('list');
    } else {
      showError('Failed to add address from QR code');
    }
  };

  const handleAddAddress = () => {
    clearMessages();
    
    if (!newAddress.trim()) {
      showError('Please enter an address');
      return;
    }
    
    if (!newNickname.trim()) {
      showError('Please enter a nickname');
      return;
    }

    if (!AddressBookService.isValidAddress(newAddress)) {
      showError('Please enter a valid wallet address');
      return;
    }

    const success = AddressBookService.setNickname(newAddress, newNickname);
    if (success) {
      setNewAddress('');
      setNewNickname('');
      loadAddressEntries();
      notifyAddressBookUpdated();
      showSuccess(`Added "${newNickname}"`);
      setActiveTab('list');
    } else {
      showError('Failed to add address nickname');
    }
  };

  const handleEditStart = (address, nickname) => {
    setEditingAddress(address);
    setEditingNickname(nickname);
    clearMessages();
  };

  const handleEditSave = () => {
    clearMessages();
    
    if (!editingNickname.trim()) {
      showError('Nickname cannot be empty');
      return;
    }

    const success = AddressBookService.setNickname(editingAddress, editingNickname);
    if (success) {
      setEditingAddress(null);
      setEditingNickname('');
      loadAddressEntries();
      notifyAddressBookUpdated();
      showSuccess('Updated successfully');
    } else {
      showError('Failed to update nickname');
    }
  };

  const handleEditCancel = () => {
    setEditingAddress(null);
    setEditingNickname('');
    clearMessages();
  };

  const handleDelete = (address) => {
    if (window.confirm('Remove this address nickname?')) {
      const success = AddressBookService.removeNickname(address);
      if (success) {
        loadAddressEntries();
        notifyAddressBookUpdated();
        showSuccess('Address removed');
      } else {
        showError('Failed to remove address');
      }
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to remove ALL address nicknames? This cannot be undone.')) {
      AddressBookService.clearAll();
      loadAddressEntries();
      notifyAddressBookUpdated();
      showSuccess('All address nicknames cleared');
    }
  };

  const handleExport = () => {
    try {
      const jsonData = AddressBookService.exportAddressBook();
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `gamemaker-address-book-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showSuccess('Address book exported successfully');
    } catch (error) {
      showError('Failed to export address book');
      console.error('Export error:', error);
    }
  };

  const handleImportFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = AddressBookService.importAddressBook(e.target.result, true);
        if (result.success) {
          loadAddressEntries();
          notifyAddressBookUpdated();
          showSuccess(result.message);
        } else {
          showError(result.message);
        }
      } catch (error) {
        showError('Failed to read import file');
      }
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    
    reader.readAsText(file);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Address Book Icon */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center justify-center w-10 h-10 bg-blue-200 border-2 border-blue-600 rounded-full hover:bg-blue-300 transition-colors"
        title={`Address Book (${addressEntries.length} entries)`}
      >
        {/* Notification badge */}
        {addressEntries.length > 0 && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
            {addressEntries.length > 99 ? '99+' : addressEntries.length}
          </div>
        )}
        
        {/* Enhanced address book icon */}
        <div className="text-2xl font-bold text-blue-800">📒</div>
      </button>

      {/* Dropdown Modal */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] sm:max-w-md bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[80vh]">
          {/* Header with tabs */}
          <div className="p-3 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-800">Address Book</h3>
              <button
                onClick={() => {
                  setShowDropdown(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Enhanced Tab buttons */}
            <div className="flex space-x-1 flex-wrap">
              <button
                onClick={() => setActiveTab('list')}
                className={`px-2 py-1 text-xs rounded transition ${
                  activeTab === 'list' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                List ({addressEntries.length})
              </button>
              <button
                onClick={() => setActiveTab('add')}
                className={`px-2 py-1 text-xs rounded transition ${
                  activeTab === 'add'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Add New
              </button>
              <button
                onClick={() => setActiveTab('qr-add')}
                className={`px-2 py-1 text-xs rounded transition ${
                  activeTab === 'qr-add'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Add via QR
              </button>
              <button
                onClick={() => setActiveTab('import-export')}
                className={`px-2 py-1 text-xs rounded transition ${
                  activeTab === 'import-export' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Import/Export
              </button>
            </div>
          </div>

          <div className="p-3">
            {/* Messages */}
            {error && (
              <div className="bg-red-50 text-red-700 text-xs p-2 rounded mb-2">
                {error}
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 text-green-700 text-xs p-2 rounded mb-2">
                {success}
              </div>
            )}

            {/* Enhanced Tab Content */}
            {activeTab === 'list' ? (
              <div className="space-y-3">
                {addressEntries.length > 0 && (
                  <div className="pb-2 border-b">
                    <span className="text-xs font-medium text-gray-700">Saved Addresses ({addressEntries.length})</span>
                  </div>
                )}
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                  {addressEntries.length === 0 ? (
                    <div className="text-center text-gray-500 py-4 text-sm">
                      No addresses saved yet
                      <br />
                      <button
                        onClick={() => setActiveTab('add')}
                        className="text-blue-600 hover:underline mt-1"
                      >
                        Add your first address
                      </button>
                    </div>
                  ) : (
                    addressEntries.map((entry) => (
                      <div key={entry.address} className="border rounded p-2 text-xs">
                        {editingAddress === entry.address ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={editingNickname}
                              onChange={(e) => setEditingNickname(e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                              placeholder="Enter nickname"
                            />
                            <div className="flex space-x-1">
                              <button
                                onClick={handleEditSave}
                                className="flex-1 bg-green-500 text-white px-2 py-1 text-xs rounded hover:bg-green-600"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleEditCancel}
                                className="flex-1 bg-gray-500 text-white px-2 py-1 text-xs rounded hover:bg-gray-600"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-gray-800">{entry.nickname}</span>
                              <span className="text-gray-500 ml-2">-</span>
                              <span className="font-mono text-gray-600 ml-2 text-xs">
                                {entry.address.slice(0, 6)}...{entry.address.slice(-4)}
                              </span>
                            </div>
                            <div className="flex space-x-1">
                              <button
                                onClick={() => navigator.clipboard.writeText(entry.address)}
                                className="w-6 h-6 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 flex items-center justify-center"
                                title="Copy address"
                              >
                                📋
                              </button>
                              <button
                                onClick={() => handleEditStart(entry.address, entry.nickname)}
                                className="w-6 h-6 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 flex items-center justify-center"
                                title="Edit nickname"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDelete(entry.address)}
                                className="w-6 h-6 bg-red-100 text-red-600 rounded hover:bg-red-200 flex items-center justify-center"
                                title="Delete entry"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : activeTab === 'add' ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={newAddress}
                    onChange={(e) => setNewAddress(e.target.value)}
                    placeholder="0x1234..."
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Nickname
                  </label>
                  <input
                    type="text"
                    value={newNickname}
                    onChange={(e) => setNewNickname(e.target.value)}
                    placeholder="Alice, Bob, etc."
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  />
                </div>
                <button
                  onClick={handleAddAddress}
                  className="w-full bg-blue-500 text-white px-3 py-2 text-xs rounded hover:bg-blue-600 transition"
                >
                  Add Address
                </button>
              </div>
            ) : activeTab === 'qr-add' ? (
              <div className="space-y-3">
                <div className="text-xs text-gray-600 mb-3">
                  Scan a QR code with your camera to add an address.
                </div>

                {/* Camera Controls */}
                {!scannedAddress && (
                  <div className="space-y-3">
                    <div className="text-center">
                      {!isScanning && (
                        <button
                          onClick={() => {
                            console.log('Start Camera clicked - setting isScanning to true');
                            setIsScanning(true);
                            clearMessages();
                          }}
                          className="px-4 py-2 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition"
                        >
                          📷 Start Camera
                        </button>
                      )}

                      {isScanning && (
                        <button
                          onClick={() => setIsScanning(false)}
                          className="px-4 py-2 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition"
                        >
                          Stop Camera
                        </button>
                      )}
                    </div>

                    {isScanning && (
                      <QRScanner
                        isActive={isScanning}
                        onScan={handleQRScanned}
                        onError={handleQRScanError}
                        className="text-center"
                      />
                    )}
                  </div>
                )}

                {/* Scanned Address Display */}
                {scannedAddress && (
                  <div className="space-y-3">
                    <div className="bg-green-50 border border-green-200 rounded p-3">
                      <h4 className="text-sm font-medium text-green-800 mb-2">📷 Scanned QR Code</h4>

                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                          <div className="text-xs font-mono bg-white p-2 rounded border break-all">
                            {scannedAddress.address}
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                          <input
                            type="text"
                            value={scannedAddress.editedName !== undefined ? scannedAddress.editedName : scannedAddress.name}
                            onChange={(e) => {
                              setScannedAddress({
                                ...scannedAddress,
                                editedName: e.target.value
                              });
                            }}
                            placeholder="Enter a name for this address"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={handleAddScannedAddress}
                        className="flex-1 px-4 py-2 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition"
                      >
                        Add to Address Book
                      </button>

                      <button
                        onClick={() => {
                          setScannedAddress(null);
                          clearMessages();
                        }}
                        className="px-4 py-2 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {!scannedAddress && !isScanning && (
                  <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                    <strong>How to scan:</strong>
                    <ol className="list-decimal list-inside mt-1 space-y-1">
                      <li>Click "📷 Start Camera" to begin scanning</li>
                      <li>Point your camera at a GameMaker QR code</li>
                      <li>The address will be detected automatically</li>
                      <li>Edit the name if needed and click "Add to Address Book"</li>
                    </ol>
                  </div>
                )}
              </div>
            ) : activeTab === 'import-export' ? (
              <div className="space-y-4">
                <div className="text-xs text-gray-600 mb-3">
                  Backup your address book or share it between devices.
                </div>
                
                <div className="grid grid-cols-1 gap-4">
                  {/* Export Section */}
                  <div className="bg-gray-50 p-3 rounded">
                    <h4 className="text-xs font-semibold mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-1 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export
                    </h4>
                    <p className="text-xs text-gray-600 mb-2">
                      Download your address book as a JSON file.
                    </p>
                    <button
                      onClick={handleExport}
                      disabled={addressEntries.length === 0}
                      className={`w-full py-2 px-3 rounded text-xs transition ${
                        addressEntries.length === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-500 text-white hover:bg-green-600'
                      }`}
                    >
                      Export Address Book
                    </button>
                  </div>

                  {/* Import Section */}
                  <div className="bg-gray-50 p-3 rounded">
                    <h4 className="text-xs font-semibold mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      Import
                    </h4>
                    <p className="text-xs text-gray-600 mb-2">
                      Import addresses from a previously exported JSON file. This will merge with existing entries.
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json"
                      onChange={handleImportFile}
                      className="w-full text-xs px-2 py-1 border border-gray-300 rounded cursor-pointer file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100"
                    />
                  </div>

                  {/* Clear All Section */}
                  <div className="bg-gray-50 p-3 rounded">
                    <h4 className="text-xs font-semibold mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-1 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Clear All Data
                    </h4>
                    <p className="text-xs text-gray-600 mb-2">
                      Remove all saved addresses from your address book. This action cannot be undone.
                    </p>
                    <button
                      onClick={handleClearAll}
                      disabled={addressEntries.length === 0}
                      className={`w-full py-2 px-3 rounded text-xs transition ${
                        addressEntries.length === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-red-500 text-white hover:bg-red-600'
                      }`}
                    >
                      Clear All Addresses ({addressEntries.length})
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressBookIcon;